import { LineActivityTime } from "../../../../services/line-activities-api";

/**
 * A time as people write it: "90", "90 min", "1.5 h", "1h 30", "1:30" → 90.
 * Blank → null (no time). Anything else → undefined (not a time).
 */
export function parseMinutes(text: string): number | null | undefined {
	const t = text.trim().toLowerCase();
	if (t === "") return null;
	let m = t.match(/^(\d+):(\d{1,2})$/);
	if (m) return Number(m[1]) * 60 + Number(m[2]);
	m = t.match(/^(\d+(?:\.\d+)?)\s*h(?:rs?|ours?)?\s*(?:(\d+)\s*(?:m|min|mins|minutes)?)?$/);
	if (m) return Math.round(Number(m[1]) * 60) + (m[2] ? Number(m[2]) : 0);
	m = t.match(/^(\d+)\s*(?:m|min|mins|minutes)?$/);
	if (m) return Number(m[1]);
	return undefined;
}

/** 90 → "1 h 30", 480 → "8 h", 45 → "45 min". */
export function formatMinutes(minutes: number | null | undefined): string {
	if (minutes === null || minutes === undefined) return "";
	if (minutes < 60) return `${minutes} min`;
	const h = Math.floor(minutes / 60);
	const m = minutes % 60;
	return m === 0 ? `${h} h` : `${h} h ${String(m).padStart(2, "0")}`;
}

/** Key of a time: activity × machine × product (blank = every product). */
export const timeKey = (activityTypeId: number, workstation: string, productId?: number | null): string =>
	`${activityTypeId}|${workstation.toLowerCase()}|${productId ?? ""}`;

export const indexTimes = (times: LineActivityTime[]): Map<string, LineActivityTime> =>
	new Map(times.map((t) => [timeKey(t.activityTypeId, t.workstation, t.productId), t]));

/** A machine's own product times, product by product. */
export function productTimesOn(times: LineActivityTime[], workstation: string): LineActivityTime[] {
	const line = workstation.toLowerCase();
	return times
		.filter((t) => t.productId !== null && t.productId !== undefined && t.workstation.toLowerCase() === line)
		.sort((a, b) => (a.productId ?? 0) - (b.productId ?? 0) || a.activityTypeId - b.activityTypeId);
}

/** The time an activity takes on a machine for a product: the product's own, else the machine's. */
export function timeFor(index: Map<string, LineActivityTime>, activityTypeId: number, workstation: string, productId?: number | null): LineActivityTime | undefined {
	return (productId ? index.get(timeKey(activityTypeId, workstation, productId)) : undefined) ?? index.get(timeKey(activityTypeId, workstation, null));
}
